import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconAlertCircle,
  IconClock,
  IconClipboardCheck,
  IconCurrencyEuro,
  IconFileText,
  IconHome,
  IconMail,
  IconShieldCheck,
  IconUserPlus,
  IconReceipt,
} from "@tabler/icons-react";
import moment from "moment/moment";
import Card from "../UI/Card";
import CardAction from "../UI/CardAction";
import DashboardCard from "../UI/DashboardCard";
import Title from "../UI/Title";
import ContentLayout from "../UI/Layout/ContentLayout";
import HeaderLayout from "../UI/Layout/HeaderLayout";
import { Tabs } from "../UI/Tabs/Tabs";
import useActiveTab from "../../hooks/use-active-tab";
import VideoTutorialsButton from "../VideoTutorials/VideoTutorialsButton";
import PriorityActions from "./PriorityActions/PriorityActions";
import OnboardingStepper from "./OnboardingStepper";
import ActivationChecklist from "./ActivationChecklist/ActivationChecklist";
// @ts-expect-error pure js
import SelectPropertyTenantAction from "../UI/SelectPropertyTenantAction";
import BulkQuittanceModal from "../Tenant/Bulk/BulkQuittanceModal";
import Events from "../Event/Events";
import Rentabilite from "./Rentabilite";
import Processes from "../Processes/Processes";
// @ts-expect-error pure js
import ProfileDocuments from "../Profile/ProfileDocuments";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useProcesses from "../../hooks/use-processes";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import { useAllTenantsBalances } from "../../hooks/use-balances.js";
// @ts-expect-error pure js
import useBalancesDashboard from "../../hooks/use-balances-dashboard.js";
import useOnboarding from "../../hooks/use-onboarding";
// @ts-expect-error pure js
import { TenantPaymentStatus, TenantStatus } from "../../models/tenant_old.js";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions.js";
import { PropertyStatus } from "../../models/property";
import type { Property } from "../../models/property";
import type { Tenant } from "../../models/tenant";
import type { TenantAccountBalance } from "../../models/balance";
import type { ProfileBailleur } from "../Profile/types";
import style from "./Dashboard.module.css";

interface TenantArrears {
  enRetard: number;
  impaye: number;
  dgRetard: number;
}

/**
 * Un locataire est « en impayé » dès que sa dette dépasse un terme complet
 * (loyer + charges) ; en deçà d'un solde négatif il est simplement en retard.
 */
function countArrears(
  tenants: Tenant[],
  balances: TenantAccountBalance[],
): TenantArrears {
  const arrears: TenantArrears = { enRetard: 0, impaye: 0, dgRetard: 0 };

  for (const tenant of tenants) {
    if (tenant.status !== TenantStatus.Actif.status) continue;
    const balance = balances.find(
      (tenantBalance) => tenantBalance.id_tenant === tenant.id,
    );
    if (!balance) continue;

    if (-balance.balance > (tenant.loyer_hc ?? 0) + (tenant.charges ?? 0)) {
      arrears.impaye++;
    } else if (balance.balance < 0) {
      arrears.enRetard++;
    }
    if (balance.dg_credit + balance.dg_debit < 0) {
      arrears.dgRetard++;
    }
  }

  return arrears;
}

export default function Dashboard() {
  const { properties, isLoading: isLoadingProperties } = useProperties() as {
    properties: Property[];
    isLoading: boolean;
  };
  const [EdlModal, , openEdlModal] = useModal();
  const [isProcessOpen, setIsProcessOpen] = useState(false);
  const [CourrierModal, , openCourrierModal] = useModal();
  const [isQuittanceModalOpen, setIsQuittanceModalOpen] = useState(false);
  const { activeProcesses, deleteProcess } = useProcesses();
  const [selectedProcessId, setSelectedProcessId] = useState<
    number | string | null
  >(null);
  const { profileBailleurs, isLoading: isLoadingProfileBailleurs } =
    useProfileBailleurs() as {
      profileBailleurs: ProfileBailleur[];
      isLoading: boolean;
    };
  const nav = useNavigate();
  const { tenants, isLoading: isLoadingTenants } = useTenants() as {
    tenants: Tenant[];
    isLoading: boolean;
  };
  const { balances } = useAllTenantsBalances(false) as {
    balances: TenantAccountBalance[];
  };
  const { revenusMois, appelesMois } = useBalancesDashboard();
  const { onboarding, isLoading: isLoadingOnboarding } = useOnboarding();
  const activeTab = useActiveTab("dash", "vue-ensemble");
  const isLoading =
    isLoadingProperties ||
    isLoadingProfileBailleurs ||
    isLoadingTenants ||
    isLoadingOnboarding;
  const hasProfile = profileBailleurs.length > 0;
  const hasProperty = properties.length > 0;
  const hasTenant = tenants.length > 0;

  if (!isLoading && !hasTenant && !onboarding.segment) {
    return (
      <ContentLayout
        header={<HeaderLayout title="" />}
        body={
          <OnboardingStepper
            hasProfile={hasProfile}
            hasProperty={hasProperty}
            hasTenant={hasTenant}
          />
        }
      />
    );
  }

  const nbCandidats = tenants.filter(
    (tenant) => tenant.status === TenantStatus.Candidat.status,
  ).length;

  const nbPropertiesLouees = tenants.filter(
    (tenant) =>
      moment().isBetween(tenant.debut, tenant.fin) &&
      tenant.status === TenantStatus.Actif.status,
  ).length;

  const nbPropertiesActives = properties.filter(
    (property) => property.status === PropertyStatus.Actif.status,
  ).length;

  const arrears = countArrears(tenants, balances);

  const body = (
    <>
      <Processes
        open={isProcessOpen}
        onOpenChange={(open) => {
          setIsProcessOpen(open);
          if (!open) setSelectedProcessId(null);
        }}
        onFinish={() => {
          setSelectedProcessId(null);
          setIsProcessOpen(false);
        }}
        initialProcessId={selectedProcessId}
      />
      <CourrierModal>
        <SelectPropertyTenantAction
          tenants={tenants}
          properties={properties}
          filtering="tenants"
          onClick={(id: number) =>
            nav(`/bailleur/tenants/${id}?tab-tenant-show=actions`)
          }
          title="Courriers - avenants - congés"
          text="Sélectionnez le locataire à qui vous souhaitez envoyer un courrier/avenant/congé"
        />
      </CourrierModal>
      <EdlModal>
        <SelectPropertyTenantAction
          tenants={tenants}
          properties={properties}
          filtering="tenants"
          onClick={(id: number) => nav(`/bailleur/tenants/${id}?action=edl`)}
          title="État des lieux"
          text="Sélectionnez le locataire pour lequel vous souhaitez faire un état des lieux."
        />
      </EdlModal>
      <BulkQuittanceModal
        open={isQuittanceModalOpen}
        onOpenChange={setIsQuittanceModalOpen}
      />
      <div className={style.dashboardCards}>
        <DashboardCard
          title="Logements loués"
          value={nbPropertiesLouees}
          valueSuffix={`/${nbPropertiesActives}`}
          icon={IconHome}
          iconColor="orange"
          onClick={() => nav("/bailleur/properties")}
        />
        <DashboardCard
          title="Candidatures"
          value={nbCandidats}
          icon={IconUserPlus}
          iconColor="info"
          onClick={() => nav("/bailleur/candidats")}
        />
        <DashboardCard
          title="Revenus locatifs"
          value={currencyFormatter(revenusMois, {
            withSymbol: false,
            noDecimals: true,
          })}
          valueSuffix={`/${currencyFormatter(appelesMois, {
            withSymbol: false,
            noDecimals: true,
          })} €`}
          subtitle="perçus / appelés ce mois-ci"
          icon={IconCurrencyEuro}
          iconColor="success"
          onClick={() => nav("/bailleur/pilotage")}
        />
        <DashboardCard
          title="Retards de paiement"
          value={arrears.enRetard}
          icon={IconClock}
          iconColor="warning"
          onClick={() =>
            nav(`/bailleur/pilotage?statut=${TenantPaymentStatus.RETARD}`)
          }
        />
        <DashboardCard
          title="Dépôts de garantie non reçus"
          value={arrears.dgRetard}
          icon={IconShieldCheck}
          iconColor="violet"
          onClick={() =>
            nav(`/bailleur/pilotage?statut=${TenantPaymentStatus.DG_IMPAYE}`)
          }
        />
        <DashboardCard
          title="Impayés"
          value={arrears.impaye}
          icon={IconAlertCircle}
          iconColor="danger"
          onClick={() =>
            nav(`/bailleur/pilotage?statut=${TenantPaymentStatus.IMPAYE}`)
          }
        />
      </div>
      {onboarding.segment && (
        <ActivationChecklist
          tenants={tenants}
          properties={properties}
          profileBailleurs={profileBailleurs}
        />
      )}
      <PriorityActions
        activeProcesses={activeProcesses}
        tenants={tenants}
        onOpenProcess={(id) => {
          setSelectedProcessId(id);
          setIsProcessOpen(true);
        }}
        onDeleteProcess={(id) => deleteProcess(id)}
      />
      <div className={style.body}>
        <Card className={style.raccourcis}>
          <Title as="h2" variant="deca" weight="bold">
            Mes raccourcis
          </Title>
          <CardAction
            onClick={() => nav("/bailleur/properties/add")}
            icon={IconHome}
            iconColor="info"
            title="Ajouter un bien"
          />
          <CardAction
            onClick={() => nav("/bailleur/tenants/add")}
            icon={IconUserPlus}
            iconColor="info"
            title="Ajouter un locataire"
          />
          <CardAction
            onClick={() => nav("/bailleur/candidats/add")}
            icon={IconUserPlus}
            iconColor="info"
            title="Inviter un candidat"
          />
          <CardAction
            onClick={() => setIsQuittanceModalOpen(true)}
            icon={IconReceipt}
            iconColor="info"
            title="Générer une quittance"
          />
          <CardAction
            onClick={() => openEdlModal()}
            icon={IconClipboardCheck}
            iconColor="info"
            title="Réaliser un état des lieux"
          />
          <CardAction
            onClick={() =>
              window.open("https://www.qalimo.fr/bibliotheque/", "_blank")
            }
            icon={IconFileText}
            iconColor="info"
            title="Modèles de document"
          />
          <CardAction
            onClick={() => openCourrierModal()}
            icon={IconMail}
            iconColor="info"
            title="Envoyer un courrier"
          />
        </Card>
        <Card>
          <Events />
        </Card>
      </div>
    </>
  );

  return (
    <ContentLayout
      header={
        <HeaderLayout
          title="Bienvenue sur Qalimo !"
          actions={
            activeTab === "vue-ensemble" && (
              <VideoTutorialsButton page="dashboard" />
            )
          }
        />
      }
      body={
        <Tabs id="dash">
          <div className="container bg-blue paddingT10 border-blue">
            <div className="content">
              <Tabs.List>
                <Tabs.Tab value="vue-ensemble">Général</Tabs.Tab>
                <Tabs.Tab value="rentabilite">Rentabilité</Tabs.Tab>
                <Tabs.Tab value="documents">Export quittances</Tabs.Tab>
              </Tabs.List>
            </div>
          </div>
          <div className="blueSpace" />
          <div className="content">
            <Tabs.Panel value="vue-ensemble">{body}</Tabs.Panel>
            <Tabs.Panel value="rentabilite">
              <Rentabilite />
            </Tabs.Panel>
            <Tabs.Panel value="documents">
              <ProfileDocuments />
            </Tabs.Panel>
          </div>
        </Tabs>
      }
    />
  );
}
